import { useState } from 'react';

interface Series {
  name: string;
  color: string;
  data: number[];
}

interface BarChartProps {
  labels: string[];
  series: Series[];
  width?: number;
  height?: number;
}

export default function BarChart({ labels, series, width = 600, height = 200 }: BarChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (labels.length === 0 || series.length === 0) {
    return (
      <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', fontSize: '14px' }}>
        No data available
      </div>
    );
  }

  // Max across all individual values (not stacked)
  const maxValue = Math.max(...series.flatMap(s => s.data), 1);
  const seriesCount = series.length;

  const padding = { top: 20, right: 10, bottom: 32, left: 38 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const slotWidth = chartWidth / labels.length;
  const groupWidth = slotWidth * 0.75;
  const subBarWidth = Math.max(3, (groupWidth - (seriesCount > 1 ? (seriesCount - 1) * 1 : 0)) / seriesCount);
  const gap = slotWidth * 0.25;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Legend */}
      {series.length > 1 && (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
          {series.map(s => (
            <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: s.color }} />
              {s.name}
            </div>
          ))}
        </div>
      )}

      <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
        {/* Grid lines + Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
          const yVal = Math.round(maxValue * pct);
          return (
            <g key={i}>
              <line
                x1={padding.left}
                y1={padding.top + chartHeight * (1 - pct)}
                x2={width - padding.right}
                y2={padding.top + chartHeight * (1 - pct)}
                stroke="var(--color-border-light)"
                strokeWidth="1"
              />
              <text
                x={padding.left - 6}
                y={padding.top + chartHeight * (1 - pct) + 3.5}
                textAnchor="end"
                fontSize="10"
                fill="var(--color-text-muted)"
              >
                {yVal >= 1000 ? `${(yVal / 1000).toFixed(1)}k` : yVal}
              </text>
            </g>
          );
        })}

        {/* Grouped bars */}
        {labels.map((label, i) => {
          const groupX = padding.left + i * slotWidth + gap / 2;
          const isHovered = hoveredIndex === i;

          return (
            <g key={i} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}>
              {/* Invisible hit area */}
              <rect x={groupX - 2} y={padding.top} width={groupWidth + 4} height={chartHeight} fill="transparent" />
              {/* Side-by-side bars */}
              {series.map((s, si) => {
                const val = s.data[i] || 0;
                const barHeight = (val / maxValue) * chartHeight;
                const barX = groupX + si * (subBarWidth + (seriesCount > 1 ? 1 : 0));
                const y = padding.top + chartHeight - barHeight;
                if (val === 0) return null;
                return (
                  <rect
                    key={si}
                    x={barX}
                    y={y}
                    width={subBarWidth}
                    height={Math.max(barHeight, 2)}
                    rx={Math.min(subBarWidth / 2, 3)}
                    fill={s.color}
                    opacity={isHovered ? 1 : 0.75}
                    style={{ transition: 'opacity 0.15s ease' }}
                  />
                );
              })}
              {/* X-axis label */}
              {(labels.length <= 14 || i % Math.ceil(labels.length / 10) === 0 || i === labels.length - 1) && (
                <text x={padding.left + i * slotWidth + slotWidth / 2} y={height - 4} textAnchor="middle" fontSize="10" fill="var(--color-text-muted)">
                  {label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {hoveredIndex !== null && (
        <div
          style={{
            position: 'absolute',
            top: '0',
            left: `${((hoveredIndex + 0.5) / labels.length) * 100}%`,
            transform: 'translateX(-50%)',
            background: 'var(--color-text-primary)',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <div style={{ marginBottom: series.length > 1 ? '2px' : 0 }}>{labels[hoveredIndex]}</div>
          {series.map(s => (
            <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 500 }}>
              {series.length > 1 && <div style={{ width: '7px', height: '7px', borderRadius: '1px', background: s.color, flexShrink: 0 }} />}
              {series.length > 1 ? `${s.name}: ` : ''}{s.data[hoveredIndex] || 0}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
